import { Injectable } from '@angular/core';
import gql from 'graphql-tag';
import { combineLatest, from, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { IFgcOvertakenIssuers } from '../../interfaces/fixedincome/fgc-overtaken.interface';
import { BackendService } from '../new-http/backend.service';

@Injectable({ providedIn: 'root' })
export class FgcOvertakenService {
    private readonly _FGC_LIMIT = 250_000;
    private readonly _PAPER_TYPE_BANCARIO = 0;

    constructor(private backendService: BackendService) {
    }

    public extractFgcOvertaken(investments: any[]): Array<IFgcOvertakenIssuers> {
        const issuerGrouped: Array<IFgcOvertakenIssuers> = [];

        for (const investment of investments) {
            if (!this.isGuaranteedByTheFgc(investment)) {
                continue;
            }

            let value = investment.currentValue + investment.blockedValue

            const issuer = issuerGrouped.find(i => i.id === investment.issuerId);

            if (!!issuer) {
                issuer.investedValue += value;
            } else {
                issuerGrouped.push({
                    id: investment.issuerId,
                    name: investment.issuerName,
                    investedValue: value,
                });
            }
        }

        return issuerGrouped.filter(item => item.investedValue > this._FGC_LIMIT);
    }

    public findAcceptedFgcOvertaken(issuers: IFgcOvertakenIssuers[]) {
        if (!issuers.length) {
            return of([]);
        }

        const query$ = this.backendService.graphqlApi.query({
            fetchPolicy: 'network-only',
            query: gql`
            query {
                userActions {
                    checkUserActions(keys: [${ this.getIssuerKeys(issuers) }]) {
                        keys {
                            key
                            value
                        }
                    }
                }
            }
        `,
        });

        return from(query$).pipe(
            map(({ data }) => {
                if (!data.userActions || !data.userActions.checkUserActions.keys) {
                    return [];
                }

                const keys = data.userActions.checkUserActions.keys || [];
                return keys
                    .filter(item => !!item.value)
                    .map(item => {
                        const id = +item.key.split('_')[0];
                        return !isNaN(id) ? id : undefined;
                    })
                    .filter(Number);
            }),
        );
    }

    public acceptFgcOvertaken(issuers: IFgcOvertakenIssuers[], acceptedList: number[]) {
        const acceptanceUpdate$ = this.acceptanceUpdate(issuers);
        const registerTrail$ = this.registerTrail(issuers);

        return combineLatest(acceptanceUpdate$, registerTrail$).pipe(
            map(() => {
                return [...acceptedList, ...issuers.map(i => i.id)];
            }),
        );
    }

    private getIssuerKeys(issuers: IFgcOvertakenIssuers[]) {
        return issuers.map(i => `"${ this.getIssuerKeyName(i.id) }"`).join();
    }

    private getIssuerKeyName(issuerId: number) {
        return `${ issuerId }_fgc_overtaken`;
    }

    private isGuaranteedByTheFgc(investment) {
        return investment.paperType === this._PAPER_TYPE_BANCARIO;
    }

    private acceptanceUpdate(issuers: IFgcOvertakenIssuers[]) {
        const keys = issuers.map(i => ({ name: this.getIssuerKeyName(i.id) }));
        const mutate$ = this.backendService.graphqlApi.mutate({
            variables: { keys },
            mutation: gql`
            mutation updateUserActions($keys: [UserActionsKey]) {
              userActions {
                registerUserActions(keys: $keys) {
                  keys {
                    key
                    value
                  }
                }
              }
            }
        `,
        });
        return from(mutate$);
    }

    private registerTrail(issuers: IFgcOvertakenIssuers[]) {
        const mutate$ = this.backendService.graphqlApi.mutate({
            variables: {
                name: 'fgc_agreement',
                origin: 'web',
                data: {
                    step: 'pos-buy',
                    issuers,
                },
            },
            mutation: gql`
            mutation registerTrail($name: String!, $origin: String!, $data: JSON) {
                trail {
                    register(
                        eventName: $name,
                        eventData: $data,
                        platformOrigin: $origin
                    ) {
                      success
                    }
                }
            }
        `,
        });
        return from(mutate$);
    }
}
